import { Column, Entity, JoinColumn, ManyToOne, OneToMany } from 'typeorm';
import { RouteTimeClassificationEnum } from '../../types/route-time-classification';
import { BaseEntity } from './base.entity';
import { RouteClientEntity } from './route-client.entity';
import { TripEntity } from './trip.entity';
import { UserEntity } from './user.entity';
import { VehicleEntity } from './vehicle.entity';
import { WarehouseEntity } from './warehouse.entity';

/**
 * The recurring delivery-route definition. A Route is a named, geographically
 * concentrated list of clients that get delivered to on some schedule (M/W/F,
 * biweekly Fridays, etc.) — see `recurrenceRule`. Each `Trip` is one day's
 * actual run of a Route.
 *
 * PD-14003: the default vehicle references the real `vehicle` table
 * (`fk_vehicle__id_default`), matching trips post-PD-13767. The legacy
 * vehicles-as-locations columns (`fk_location__id_default_vehicle`,
 * `fk_location__id_default`) are dropped by the cutover migration —
 * never reintroduce them.
 */
@Entity('route')
export class RouteEntity extends BaseEntity {
	@Column({ name: 'name', type: 'varchar', length: 255 })
	public name!: string;

	@Column({ name: 'description', type: 'text', nullable: true })
	public description?: string;

	@Column({
		name: 'fk_user__id_default_driver',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public defaultDriverUserId?: number;

	@Column({
		name: 'fk_warehouse__id_default',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public defaultWarehouseId?: number;

	@Column({
		name: 'fk_vehicle__id_default',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public defaultVehicleId?: number;

	@Column({
		name: 'recurrenceRule',
		type: 'varchar',
		length: 500,
		nullable: true,
	})
	public recurrenceRule?: string;

	/**
	 * Date this route becomes eligible to dispatch. The dispatch wizard
	 * only surfaces this route on dates `>= effectiveDate`. Combined
	 * with `expirationDate` and `recurrenceRule`, the route runs on a
	 * given date D iff:
	 *   `effectiveDate <= D <= (expirationDate ?? +∞)` AND
	 *   RRULE generates an occurrence on D.
	 *
	 * Allows scheduled updates ("change route X starting next month")
	 * and archiving (set `expirationDate` to lock the route out of
	 * future dispatches without deleting historical trip references).
	 * Replaces the previous `recurrenceStartDate` column.
	 */
	@Column({ name: 'effectiveDate', type: 'date', nullable: true })
	public effectiveDate?: Date;

	/**
	 * Last date this route is eligible to dispatch. NULL = never
	 * expires (the common case). Set to a date in the future to schedule
	 * a route's archival; set to a past date to retire it immediately.
	 */
	@Column({ name: 'expirationDate', type: 'date', nullable: true })
	public expirationDate?: Date;

	@Column({ name: 'defaultStartTime', type: 'time', nullable: true })
	public defaultStartTime?: string;

	@Column({ name: 'isActive', type: 'boolean', default: true })
	public isActive!: boolean;

	/**
	 * PD-15500 — archived routes leave the routes list by default and stop
	 * dispatching. NULL = not archived.
	 *
	 * Deliberately distinct from the two lifecycle fields above, which it does
	 * not replace:
	 *   - `isActive` is a PAUSE — a route someone means to run again, which
	 *     should stay visible while it is switched off.
	 *   - `expirationDate` is a SCHEDULE — the date a route stops being
	 *     eligible, set in advance.
	 *   - `archivedDate` is RETIREMENT — the route is done with, and the
	 *     operator wants it out of sight.
	 *
	 * Trips already generated from an archived route are untouched and stay
	 * visible; archiving is about the blueprint, not its history. Mirrors the
	 * same column on clients (PD-15452) and trips.
	 */
	@Column({ name: 'archivedDate', type: 'datetime', nullable: true })
	public archivedDate?: Date | null;

	/**
	 * PD-15449 — when true (default), stops dispatched from this route are
	 * placed inside each client's business hours (client_hours): out-of-hours
	 * placements are refused/skipped, and clients with no recorded hours are
	 * placed with a "no business hours" warning instead of being blocked.
	 * A client's own deliverDuringBusinessHoursOnly (nullable) overrides this
	 * per client in either direction.
	 */
	@Column({
		name: 'deliverDuringBusinessHoursOnly',
		type: 'boolean',
		default: true,
	})
	public deliverDuringBusinessHoursOnly!: boolean;

	/**
	 * Day/night classification (Phase C Rule 3). The dispatch wizard
	 * filters (client, route) pairs against this column and
	 * `Client.nightTimeEligibility` — a DAY_ONLY client can't be on
	 * a NIGHT route, etc. Defaults to DAY so existing routes stay
	 * compatible with the DAY_ONLY default on Client.
	 */
	@Column({
		name: 'timeClassification',
		type: 'enum',
		enum: RouteTimeClassificationEnum,
		default: RouteTimeClassificationEnum.DAY,
	})
	public timeClassification!: RouteTimeClassificationEnum;

	@ManyToOne(() => UserEntity, { nullable: true })
	@JoinColumn({ name: 'fk_user__id_default_driver' })
	public defaultDriver?: UserEntity;

	@ManyToOne(() => WarehouseEntity, { nullable: true })
	@JoinColumn({ name: 'fk_warehouse__id_default' })
	public defaultWarehouse?: WarehouseEntity;

	@ManyToOne(() => VehicleEntity, { nullable: true })
	@JoinColumn({ name: 'fk_vehicle__id_default' })
	public defaultVehicle?: VehicleEntity;

	@OneToMany(() => RouteClientEntity, (rc) => rc.fkRoute)
	public routeClients?: Array<RouteClientEntity>;

	@OneToMany(() => TripEntity, (trip) => trip.fkRoute)
	public trips?: Array<TripEntity>;
}
